import { Button, Input, Select, Table } from '@scuf/common';
import i18next from 'i18next';
import { v4 as uuid4 } from 'uuid';
import { useEffect, useState } from 'react';
import { consumeAPIs, IAPIPackage } from '../../commonUtils/APIContext';
import metertemplateData from './MeterTemplate.json';
import { EInputType, ICreateTemplatePayoad, IPayloadProperty, IPropertyObject, IProtocolObject, ITemplateDetail } from './TemplateModel';
import './FillTemplateDetail.scss';
import useFetch, { IUseFetch } from '../../commonUtils/useFetch';
import toasts from '../../commonUtils/toasts';
import { ConfirmationDialog, ConfirmationDialogType, UnitUtility } from '@bmsp/bmsp.web.component.common';
import { IUnit4 } from '../../commonUtils/UnitModel';

interface IFillTemplateDetailProps extends IAPIPackage {
  deviceType: string;
  isEditTemplate: boolean;
  isViewTemplate: boolean;
  templateData?: ITemplateDetail;
  onCancel: () => void;
  onSave: (templateId: string) => void;
}

export const FillTemplateDetail = (props: IFillTemplateDetailProps) => {
  const [disableSave, setDisableSave] = useState<boolean>(true);
  const [clickSave, setClickSave] = useState<boolean>(false);
  const [templateId, setTemplateId] = useState<string>('');
  const [templateName, setTemplateName] = useState<string>('');
  const [modelNames, setModelNames] = useState<string>('');
  const [vendorName, setVendorName] = useState<string>('');
  const [templateDetail, setTemplateDetail] = useState<IPayloadProperty[]>([]);
  const [hasErrorTemplateName, setHasErrorTemplateName] = useState<boolean>(false);
  const [hasErrorModelNames, setHasErrorModelNames] = useState<boolean>(false);
  const [hasErrorVendorName, setHasErrorVendorName] = useState<boolean>(false);
  const [showAlert, setShowAlert] = useState<boolean>(false);

  const getDefaultConfigValue = () => {
    let protocolConfigList: IPropertyObject[] = [];
    metertemplateData.propertyName.forEach(property => {
      let propertyObject: IPropertyObject = {
        propertyRoleName: property,
        protocolConfig: []
      };
      let propertyDetail = templateDetail?.find(x => x.protocolConfig && x.protocolConfig.protocolObject.objectName === property);
      metertemplateData.protocolObject.forEach(config => {
        let propertyValue;
        if (propertyDetail?.protocolConfig?.protocolObject) {
          for (let [key, value] of Object.entries(propertyDetail?.protocolConfig?.protocolObject)) {
            if (key === config.fieldName) {
              propertyValue = value;
              break;
            }
          }
        }
        let configOption = config.options ? config.options : [];
        let configVal = (propertyValue !== null && propertyValue !== undefined && propertyValue !== '') ? propertyValue.toString() : config.value ? config.value : '';
        if (config.fieldName === 'unitName') {
          let tempUnitMapping = metertemplateData.unitMapping.find(x => x.propertyName === property);
          if (tempUnitMapping) {
            let unitList = UnitUtility.getMeasurementTypeObject(tempUnitMapping.unitName);
            if (unitList && Array.isArray(unitList.unit)) {
              configOption = unitList.unit.map((unit: any) => {
                return {
                  text: unit._name,
                  value: unit._name
                };
              });
            }
            else {
              let unit: IUnit4 = unitList?.unit as IUnit4;
              configOption.push({ text: unit._name, value: unit._name });
            }
          }
        }
        else if (config.fieldName === 'objectName') {
          configVal = property;
        }
        else {
          // do nothing
        }
        let configObject: IProtocolObject = {
          fieldName: config.fieldName,
          inputType: config.inputType as EInputType,
          editable: config.editable,
          hasError: false,
          value: configVal,
          options: configOption,
          validation: config.validation,
          errorMsg: '',
          isUnique: config.isUnique
        };
        propertyObject.protocolConfig.push(configObject);
      });
      protocolConfigList.push(propertyObject);
    });
    return protocolConfigList;
  };

  const [propertyConfigData, setPropertyConfigData] = useState<
    IPropertyObject[]
  >(getDefaultConfigValue());

  useEffect(() => {
    if (props.templateData) {
      setTemplateName(props.templateData.name);
      setTemplateId(props.templateData.id);
      setModelNames(props.templateData.modelName);
      setVendorName(props.templateData.vendorName);
      setTemplateDetail(props.templateData.templateConfiguration.propertyAttributes);
    } else {
      setTemplateName('');
      setTemplateId('');
      setModelNames('');
      setVendorName('');
      setTemplateDetail([]);
    }
  }, [props]);

  useEffect(() => {
    setPropertyConfigData(getDefaultConfigValue())
  }, [templateDetail]);

  const [
    postSaveTemplateResponse,
    isLoadingPostSaveTemplate,
    errorPostSaveTemplate,
    saveTemplate
  ]: IUseFetch<any> = useFetch(
    `${props.baseURL}/api/v1/standardtemplates`,
    props.authFetch,
    undefined,
    true
  );

  const [
    postUpdateTemplateResponse,
    isLoadingPostUpdateTemplate,
    errorPostUpdateTemplate,
    updateTemplate
  ]: IUseFetch<any> = useFetch(
    `${props.baseURL}/api/v1/standardtemplates/${props.templateData?.id}`,
    props.authFetch,
    undefined,
    true
  );
  useEffect(() => {
    checkDisableSave();
  }, [hasErrorModelNames, hasErrorTemplateName, hasErrorVendorName]);

  const getRequestPayloadForCreate = () => {
    let propArray: IPayloadProperty[] = [];

    propertyConfigData.forEach(property => {
      let configObj: any = {};
      property.protocolConfig.forEach(config => {
        configObj[config.fieldName] = config.value;
      });
      let payloadProperty: IPayloadProperty = {
        propertyName: property.propertyRoleName + '.PresentValue',
        protocolConfig: {
          protocolType: 'modbus',
          protocolObject: configObj
        }
      };
      propArray.push(payloadProperty);
    });
    const id = props.isEditTemplate ? props.templateData &&  props.templateData.id ? props.templateData.id : uuid4() : uuid4();
    let createTemplatePayoad: ICreateTemplatePayoad = {
      id: id,
      deviceType: props.deviceType,
      templateName: templateName,
      modelNames: [modelNames],
      vendorName: vendorName,
      templateConfiguration: {
        propertyAttributes: propArray
      }
    };
    return createTemplatePayoad;
  };

  useEffect(() => {
    if (
      !isLoadingPostSaveTemplate &&
      errorPostSaveTemplate === undefined &&
      postSaveTemplateResponse !== null
    ) {
      toasts.success(i18next.t('addTemplateSuccess'));
      props.onSave(postSaveTemplateResponse?.resource?.id);
    } else {
      if (errorPostSaveTemplate) {
        setDisableSave(false);
        toasts.error(i18next.t('addTemplateError'));
      }
    }
  }, [isLoadingPostSaveTemplate]);

  useEffect(() => {
    if (
      !isLoadingPostUpdateTemplate &&
      errorPostUpdateTemplate === undefined &&
      postUpdateTemplateResponse !== null
    ) {
      toasts.success(i18next.t('updateTemplateSuccess'));
      props.onSave(postUpdateTemplateResponse?.resource?.id);
    } else {
      if (errorPostUpdateTemplate) {
        setDisableSave(false);
        toasts.error(i18next.t('updateTemplateError'));
      }
    }
  }, [isLoadingPostUpdateTemplate]);

  const clickTemplateDetails = () => {
    if (!disableSave) {
      saveTemplateDetails();
    } else {
      let list = [...propertyConfigData];
      let valArray = list.find(a => a.protocolConfig.find(x => x.value === ''));
      
      if (valArray) {
        setClickSave(true);
        toasts.error(i18next.t('requiredField'));
      }
    }
  }
  const saveTemplateDetails = () => {
    setDisableSave(true);
    const saveTemplateObject = {
      method: props.isEditTemplate ? 'PUT' : 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(getRequestPayloadForCreate())
    };
    if (props.isEditTemplate) {
      updateTemplate(saveTemplateObject);
    } else {
      saveTemplate(saveTemplateObject);
    }
  };

  const checkDisableSave = () => {
    let errorObj = propertyConfigData.map(a => a.protocolConfig).some(b => b.some(c => c.hasError || c.value === ''));
    if (errorObj || hasErrorModelNames || hasErrorTemplateName || hasErrorModelNames || templateName === '' || modelNames === '' || vendorName === '') {
      setDisableSave(true);
    }
    else {
      setDisableSave(false);
    }
  };

  const onTemplateNameChange = (val: string) => {
    setTemplateName(val);
    if (val === '') {
      setHasErrorTemplateName(true);
    }
    else {
      setHasErrorTemplateName(false);
    }
    checkDisableSave();
  };

  const onModelNameChange = (val: string) => {
    setModelNames(val);
    if (val === '') {
      setHasErrorModelNames(true);
    }
    else {
      setHasErrorModelNames(false);
    }
    checkDisableSave();
  };

  const onVendorNameChange = (val: string) => {
    setVendorName(val);
    if (val === '') {
      setHasErrorVendorName(true);
    }
    else {
      setHasErrorVendorName(false);
    }
    checkDisableSave();
  };

  const validateNumber = (obj: IProtocolObject, propertyName: string, val: string, tempArray: IPropertyObject[]) => {
    const regExp = new RegExp('^[0-9]+$');
    let rangeError = false;
    if (regExp.test(val)) {
      obj.value = val;
      if (obj.validation) {
        let minVal = obj.validation.find(x => x.type === 'minValue')?.value || Number.NEGATIVE_INFINITY;
        let maxVal = obj.validation.find(x => x.type === 'maxValue')?.value || Number.POSITIVE_INFINITY;
        if (Number(val) < minVal || Number(val) > maxVal) {
          obj.hasError = true;
          rangeError = true;
          obj.errorMsg = i18next.t('outOfRangeError', { min: minVal.toString(), max: maxVal.toString() });
        }
        else {
          obj.hasError = false;
          obj.errorMsg = '';
        }
      }
    }
    obj = validateUniqueness(obj, tempArray, val, rangeError);
    return obj;
  };

  const validateUniqueness = (obj: IProtocolObject, tempArray: IPropertyObject[], val: string, rangeError: boolean) => {
    let valArray = tempArray.map(a => {
      let temp = a.protocolConfig;
      return temp.find(b => b.fieldName === obj?.fieldName);
    });
    let list = valArray.map(a => a?.value);
    valArray.forEach(item => {
      if (list.filter(a => a === item?.value && item?.value !== '').length > 1) {
        if (item) {
          item.hasError = true;
          item.errorMsg = i18next.t('uniqueValueError');
        }
      }
      else {
        if (item && item?.value !== '') {
          if (rangeError && obj === item) {
            item.hasError = true;
          }
          else {
            item.hasError = false;
            item.errorMsg = '';
          }
        }
      }
    });
    return obj;
  };

  const onInputBoxChange = (config: IProtocolObject, propertyName: string, val: string) => {
    let tempArray = [...propertyConfigData];
    let obj = tempArray.find(item => item.propertyRoleName === propertyName)?.protocolConfig.find(configObj => configObj.fieldName === config.fieldName);
    if (val === '' && obj) {
      obj.hasError = true;
      obj.errorMsg = '';
      obj.value = val;
    }
    if (obj) {
      if (obj.inputType === EInputType.number) {
        obj = validateNumber(config, propertyName, val, tempArray);
      }
      else {
        obj.hasError = false;
        obj.errorMsg = '';
        obj.value = val;
      }
    }
    let errorObj = tempArray.map(a => a.protocolConfig).some(b => b.some(c => c.hasError || c.value === ''));
    if (errorObj || hasErrorModelNames || hasErrorTemplateName || hasErrorModelNames || templateName === '' || modelNames === '' || vendorName === '') {
      setDisableSave(true);
    }
    else {
      setDisableSave(val.toString().length === 0 || val === '-' || val.toString().indexOf('.') === val.toString().length - 1);
    }
    setPropertyConfigData(tempArray);
  };

  const getColumnData = (object: IPropertyObject, propertyRoleName: string) => {
    return object.protocolConfig.map(item => {
      if (item.editable) {
        switch (item.inputType) {
          case EInputType.text:
            return (
              <Table.Cell className="table-body-cell" width={16} key={item.fieldName + propertyRoleName}>
                <Input
                  className={props.isViewTemplate ? "view-template-control" : (clickSave && !item.hasError && item.value.toString() === '') || item.hasError ? "template-input custom-border" : "template-input"}
                  value={item.value.toString()}
                  onChange={val => onInputBoxChange(item, propertyRoleName, val)}
                  error={item.hasError ? item.errorMsg : undefined}
                  disabled={props.isViewTemplate}
                />
              </Table.Cell>
            );
          case EInputType.number:
            return (
              <Table.Cell className="table-body-cell" width={1} key={item.fieldName + propertyRoleName}>
                <Input
                  className={props.isViewTemplate ? "view-template-control" : (clickSave && !item.hasError && item.value.toString() === '') || item.hasError ? "template-input custom-border" : "template-input"}
                  value={item.value.toString()}
                  onChange={val => onInputBoxChange(item, propertyRoleName, val)}
                  error={item.hasError ? item.errorMsg : undefined}
                  disabled={props.isViewTemplate}
                />
              </Table.Cell>
            );
          case EInputType.select:
            return (
              <Table.Cell className="table-body-cell" key={item.fieldName + propertyRoleName}>
                <Select className={props.isViewTemplate ? "view-template-control" : (clickSave && !item.hasError && item.value.toString() === '') || item.hasError ? "template-input custom-border" : "template-input"} disabled={!item.editable || props.isViewTemplate} value={item.value} options={item.options ? item.options : []} onChange={val => onInputBoxChange(item, propertyRoleName, val)}
                  error={item.hasError ? item.errorMsg : undefined} />
              </Table.Cell>
            );
          default:
            break;
        }
      }
    });
  };

  const onConfirmDialogClick = (confirm: boolean) => {
    if (confirm) {
      props.onCancel();
    } else {
      // Do Nothing
    }
    setShowAlert(false);
  }
  return (
    <div className="template-create-view">
      <div className='template-heading template-metadata'>
        <Input
          className={(clickSave && templateName.toString() === '') || hasErrorTemplateName ? "metadata-input custom-border" : "metadata-input"}
          value={templateName}
          label={i18next.t('templateName')}
          disabled={props.isViewTemplate || props.isEditTemplate}
          onChange={val => onTemplateNameChange(val)}
        />
        <Input
          className={(clickSave && vendorName.toString() === '') || hasErrorVendorName ? "metadata-input custom-border" : "metadata-input"}
          value={vendorName}
          label={i18next.t('vendorName')}
          disabled={props.isViewTemplate || props.isEditTemplate}
          onChange={val => onVendorNameChange(val)}
        />
        <Input
          className={(clickSave && modelNames.toString() === '') || hasErrorModelNames ? "metadata-input custom-border" : "metadata-input"}
          value={modelNames}
          label={i18next.t('modelName')}
          disabled={props.isViewTemplate || props.isEditTemplate}
          onChange={val => onModelNameChange(val)}
        />
      </div>
      <Table className="template-list-table">
        <Table.Header>
          <Table.HeaderCell className="table-header sticky-first-column" content={i18next.t('propertyName')} />
          {propertyConfigData[0].protocolConfig.map(item => (
            item.editable && <Table.HeaderCell
              key={item.fieldName}
              className="table-header"
              content={i18next.t(item.fieldName)}
            />
          ))}
        </Table.Header>
        <Table.Body className="template-table-body">
          {propertyConfigData.map(object => {
            return (
              <Table.Row key={object.propertyRoleName}>
                <Table.Cell className="table-body-cell sticky-first-column" key={object.propertyRoleName}>
                  <div className='propertyName'>
                    {i18next.t(object.propertyRoleName)}
                  </div>
                </Table.Cell>
                {getColumnData(object, object.propertyRoleName)}
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
      {
        !props.isViewTemplate && <div className="template-actions">
          <Button disabled={isLoadingPostSaveTemplate || isLoadingPostUpdateTemplate} content={i18next.t('cancel')} type="secondary" onClick={() => setShowAlert(true)} />
          <Button content={i18next.t(props.isEditTemplate ? 'update' : 'save')} onClick={clickTemplateDetails} loading={isLoadingPostSaveTemplate || isLoadingPostUpdateTemplate} />
        </div>
      }
      <ConfirmationDialog message={i18next.t('warningMessage')} title={i18next.t('warningHeader')} type={ConfirmationDialogType.WARNING} open={showAlert} onClick={onConfirmDialogClick} />
    </div>
  );
};

export default consumeAPIs(FillTemplateDetail);
